import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { crypto as cryptoShared } from '@photo-vault/shared';
import type { dto } from '@photo-vault/shared';
import {
  AntiAutofillHoneypot,
  Button,
  Card,
  Icon,
  Input,
  PageHeader,
  ProviderIcon,
} from '../../ui/index.js';
import { useAuth } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { config } from '../../lib/config.js';
import { stashRecoveryCode } from './recoveryStorage.js';

type Step = 'idle' | 'cognito' | 'identity' | 'persisting' | 'done';

interface FormState {
  email: string;
  password: string;
  passphrase: string;
  passphraseConfirm: string;
}

const initial: FormState = {
  email: '',
  password: '',
  passphrase: '',
  passphraseConfirm: '',
};

export function SignupPage() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const { auth, metadata } = useInfra();

  const [form, setForm] = useState<FormState>(initial);
  const [step, setStep] = useState<Step>('idle');
  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (form.passphrase !== form.passphraseConfirm) {
      setError('Le due passphrase non coincidono.');
      return;
    }
    if (form.passphrase.length < 12) {
      setError('La passphrase deve essere di almeno 12 caratteri.');
      return;
    }
    if (form.password.length < 12) {
      setError('La password Cognito deve essere di almeno 12 caratteri.');
      return;
    }

    try {
      setStep('cognito');
      await auth.signUp(form.email, form.password);
      const cognito = await auth.signIn(form.email, form.password);

      setStep('identity');
      const setup = await cryptoShared.generateIdentity(form.passphrase);

      setStep('persisting');
      const body: dto.RegisterRequest = setup.identity;
      const registered = await metadata.registerIdentity(body, cognito.idToken);

      setSession({
        userId: registered.userId,
        email: registered.email,
        jwt: cognito.idToken,
        masterKey: setup.masterKey,
        rsaPriv: setup.rsaKeys.privateKey,
        rsaPubB64: setup.identity.rsaPubB64,
        saltB64: setup.identity.saltB64,
        provider: 'native',
      });

      stashRecoveryCode(setup.recoveryCode, 'signup');
      setStep('done');
      navigate('/recovery-code', { replace: true });
    } catch (err) {
      setStep('idle');
      setError(err instanceof Error ? err.message : 'Registrazione fallita.');
    }
  }

  const busy = step !== 'idle' && step !== 'done';

  return (
    <div className="flex w-full max-w-md flex-col gap-7">
      <PageHeader
        eyebrow="nuovo vault"
        title={
          <>
            Crea il tuo <span className="accent-text">vault personale</span>
          </>
        }
        description="La passphrase non lascia mai il tuo dispositivo. Chiave master, coppia RSA e codice di recupero generati esclusivamente in locale."
        align="center"
      />

      <Card className="flex flex-col gap-5 sm:p-7">
        {config.cognitoHostedDomain ? (
          <div className="flex flex-col gap-3">
            <Button
              type="button"
              variant="outline"
              size="lg"
              fullWidth
              leftIcon={<ProviderIcon provider="google" />}
              onClick={() => auth.startFederatedLogin('Google')}
            >
              Continua con Google
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              fullWidth
              leftIcon={<ProviderIcon provider="apple" />}
              onClick={() => auth.startFederatedLogin('SignInWithApple')}
            >
              Continua con Apple
            </Button>
            <div className="flex items-center gap-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-on-surface-variant">
              <span className="h-px flex-1 bg-outline" />
              oppure email
              <span className="h-px flex-1 bg-outline" />
            </div>
          </div>
        ) : null}
        <form className="flex flex-col gap-3" onSubmit={handleSubmit} autoComplete="off">
          <AntiAutofillHoneypot />
          <Input
            type="email"
            label="Email"
            placeholder="me@example.com"
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
            leftIcon={<Icon name="alternate_email" className="text-base" />}
            autoComplete="email"
            required
          />
          <Input
            type="password"
            label="Password Cognito"
            placeholder="min. 12 caratteri"
            value={form.password}
            onChange={(e) => update('password', e.target.value)}
            leftIcon={<Icon name="key" className="text-base" />}
            autoComplete="new-password"
            required
            hint="Usata solo per il login Cognito; non protegge i tuoi file."
          />
          <Input
            type="password"
            label="Passphrase E2EE"
            placeholder="frase lunga, mai inviata al server"
            value={form.passphrase}
            onChange={(e) => update('passphrase', e.target.value)}
            leftIcon={<Icon name="lock" className="text-base" />}
            autoComplete="new-password"
            required
            hint="Deriva la chiave master che protegge tutte le foto."
          />
          <Input
            type="password"
            label="Conferma passphrase"
            placeholder="ripeti la passphrase"
            value={form.passphraseConfirm}
            onChange={(e) => update('passphraseConfirm', e.target.value)}
            leftIcon={<Icon name="check" className="text-base" />}
            autoComplete="new-password"
            required
          />

          {error ? (
            <div className="rounded-lg border border-error/60 bg-error-container/30 px-4 py-3 text-sm text-error">
              {error}
            </div>
          ) : null}

          <Button
            type="submit"
            size="lg"
            fullWidth
            loading={busy}
            rightIcon={!busy ? <Icon name="arrow_forward" className="text-base" /> : null}
          >
            {busy ? labelForStep(step) : 'Crea il vault'}
          </Button>

          <p className="pt-2 text-center text-sm text-on-surface-variant">
            Hai già un vault?{' '}
            <button
              type="button"
              className="font-semibold text-cyan transition-colors hover:underline"
              onClick={() => navigate('/login')}
            >
              Accedi
            </button>
          </p>
        </form>
      </Card>
    </div>
  );
}

function labelForStep(step: Step): string {
  switch (step) {
    case 'cognito':
      return 'Creo l’account Cognito…';
    case 'identity':
      return 'Genero le chiavi sul dispositivo…';
    case 'persisting':
      return 'Salvo i metadati cifrati…';
    case 'done':
      return 'Fatto';
    default:
      return 'Attendi…';
  }
}
